import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
    TextField,
    Modal,
    Box,
    CircularProgress,
    Select,
    MenuItem,
} from "@mui/material";
import cogoToast from "cogo-toast";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import bg0 from "../assets/images/leadMagnet/bg0.svg";
import bg1 from "../assets/images/leadMagnet/bg1.svg";
import bg2 from "../assets/images/leadMagnet/bg2.svg";
import blueBack from "../assets/images/leadMagnet/blue-back.png";
import {
    getLeadMagnet,
    postLeadMagnetResponseForm,
    downloadLeadMagnet,
} from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import { images } from "../constants/images";
import { Helmet } from "react-helmet-async";

// Modal styles
const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "500px" },
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: 4,
    outline: "none",
};

// Hero Section Component
const HeroSection = ({
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [],
    offeringType,
}) => (
    <section
        className="page-hero-sec"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            minHeight: "80vh",
            display: "flex",
            alignItems: "center",
        }}
    >
        <div className="container mb-5">
            <div className="row align-items-center">
                <div className="col-lg-7 col-md-12">
                    <div className="hero-content text-white">
                        <h1
                            className="head-font mb-4"
                            style={{
                                fontSize: "3.5rem",
                                fontWeight: "bold",
                                lineHeight: "1.2",
                            }}
                        >
                            The Smart Founder’s Guide to Choosing the Right Software Partner
                        </h1>
                        <p
                            className="fs-17 mb-4 text-warning"
                            style={{ lineHeight: "1.6", fontWeight: "bold" }}
                        >
                            Don’t Let the Wrong Tech Partner Burn Your Budget.
                            Download the Smart Founder’s Guide to Choosing the Right Software Partner (Free eBook).

                        </p>
                        <p
                            className="fs-17 mb-4 text-white"
                            style={{ lineHeight: "1.6" }}
                        >

                            A 6-page, no-fluff guide that helps you spot red flags, ask the right questions, and pick an agency you can actually trust.

                        </p>
                        <p
                            className="fs-17 text-white"
                            style={{ lineHeight: "1.6", fontStyle: "italic" }}
                        >
                            Written for non-technical founders who want clarity before committing their money.
                        </p>
                    </div>
                </div>
                <div className="col-lg-5 col-md-12">
                    <div
                        className="hero-form bg-white p-4 rounded shadow-lg"
                        style={{ maxWidth: "400px", margin: "0 auto" }}
                    >
                        <div className="text-center mb-4">
                            <h5 className="tc-primary head-font mb-2">
                                Get Your Free Guide Now
                            </h5>
                            <p className="text-muted small">
                                Download instantly - no spam, just clarity
                            </p>
                        </div>


                        {(!isFormSubmitted ||
                            (offeringType === "E-Book")) && (
                                <>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            error={showError && !formData.name.trim()}
                                            helperText={
                                                showError && !formData.name.trim()
                                                    ? "Name is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            error={showError && !formData.email.trim()}
                                            helperText={
                                                showError && !formData.email.trim()
                                                    ? "Valid email is required"
                                                    : ""
                                            }
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            name="phone"
                                            type="text"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            error={showError && !formData.phone.trim()}
                                            helperText={
                                                showError && !formData.phone.trim()
                                                    ? "Valid phone is required"
                                                    : ""
                                            }
                                            inputProps={{ maxLength: 10 }}
                                            sx={{ backgroundColor: "#f8fafc" }}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <Select
                                            fullWidth
                                            name="nicheType"
                                            value={formData.nicheType}
                                            onChange={handleInputChange}
                                            displayEmpty
                                            error={showError && !formData.nicheType}
                                            sx={{
                                                "& .MuiSelect-select": {
                                                    background: "#e0e5ef",
                                                    padding: "12px 14px",
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                Industry Type
                                            </MenuItem>
                                            {nicheTypes?.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {showError && !formData.nicheType && (
                                            <p
                                                style={{
                                                    color: "#d32f2f",
                                                    fontSize: "0.75rem",
                                                    margin: "3px 14px 0",
                                                }}
                                            >
                                                Industry type is required
                                            </p>
                                        )}
                                    </div>

                                    {formData.nicheType === "Other" && (
                                        <div className="mb-4">
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="Specify Your Industry"
                                                name="industry_other"
                                                value={formData.industry_other}
                                                onChange={handleInputChange}
                                                error={
                                                    showError &&
                                                    formData.nicheType === "Other" &&
                                                    !formData.industry_other.trim()
                                                }
                                                helperText={
                                                    showError &&
                                                        formData.nicheType === "Other" &&
                                                        !formData.industry_other.trim()
                                                        ? "Please specify your industry"
                                                        : ""
                                                }
                                                required={formData.nicheType === "Other"}
                                                margin="normal"
                                                inputProps={{ maxLength: 100 }}
                                            />
                                        </div>
                                    )}

                                    <button
                                        onClick={submitLeadForm}
                                        className="LM_link_btn1"
                                        disabled={isSubmitting}
                                        style={{
                                            background: "#0a2458",
                                            border: "none",
                                            padding: "12px",
                                            fontWeight: "600",
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ color: "white", mr: 1 }}
                                                />
                                                Submitting...
                                            </>
                                        ) : (
                                            " Get Your Free E-Book"
                                        )}
                                    </button>

                                    <p className="text-center text-muted small mt-2 mb-0">
                                        100% privacy guaranteed. No spam ever.
                                    </p>
                                </>
                            )}
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const WhatYouWillLearn = ({ openModal }) => {
    return (
        <section
            className="d-flex align-items-center justify-content-center min-vauto py-5"
            style={{
                backgroundImage: `url(${bg0})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div className="container mt-5">
                <div className="row text-center mb-5 justify-content-center">
                    <div className="col-lg-10">
                        <h2 className="tc-primary text-center head-font mb-4">
                            What You'll Learn Inside
                        </h2>

                        <ul
                            className="text-start"
                            style={{
                                listStyle: "none",
                                fontFamily: "Inter",
                                fontWeight: 400,
                                fontSize: "20px",
                                lineHeight: "36px",
                                color: "#000000",
                                maxWidth: "750px",
                                margin: "0 auto",
                            }}
                        >
                            <li>✅ 10 warning signs that scream "run from this agency."</li>
                            <li>✅ How to read a proposal like a pro (even if you don't speak tech).</li>
                            <li>✅ The difference between a good dev partner and a smooth-talker.</li>
                            <li>✅ How to protect your code, your timeline, and your peace of mind.</li>
                            <li>✅ A self-evaluation checklist to score any agency before you hire them.</li>
                        </ul>

                        <button onClick={openModal} className="LM_link_btn mt-4">
                            Get Your Free E-Book
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

const WhoIsThisFor = ({ openModal }) => (
    <section
        className="d-flex align-items-center justify-content-center min-vauto"
        style={{
            backgroundImage: `url(${bg1})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container mt-1 mb-5">
            <div className="row justify-content-center mt-5">
                <div className="col-lg-6">
                    <img
                        src={images.ebook1}
                        alt="Software Partner Guide"
                        className="top-0 start-0 w-100 auto"
                        style={{ objectFit: "cover" }}
                    />
                </div>
                <div className="col-lg-6 align-self-center">
                    <h2 className="tc-primary text-center head-font mb-4">
                        Who This Is For :
                    </h2>

                    <ul
                        className="text-center"
                        style={{
                            listStyle: "none",
                            fontFamily: "Inter",
                            fontWeight: 400,
                            fontSize: "22px",
                            lineHeight: "36px",
                            color: "#000000",
                            padding: "2rem 0",
                        }}
                    >
                        <li style={{ fontSize: 20, marginBottom: "1rem" }}>
                            👉 Founders planning to build an app, web platform, or internal tool.
                        </li>
                        <li style={{ fontSize: 20, marginBottom: "1rem" }}>
                            👉 Business owners tired of vague timelines and overshooting budgets.
                        </li>
                        <li style={{ fontSize: 20 }}>
                            👉 Anyone who's hiring a software partner for the first time and doesn't know where to start.
                        </li>
                    </ul>

                    <div className="text-center">
                        <button onClick={openModal} className="LM_link_btn">
                            Get Your Free E-Book
                        </button>
                    </div>
                    <br /> <br />
                </div>
            </div>
        </div>
    </section>
);

const WhyItsDifferent = ({ openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <h3 className="tc-primary head-font mb-4 text-center text-white">
                Why This Guide Is Different
            </h3>

            <div className="text-center text-white">
                <p
                    className="mt-4"
                    style={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "24px",
                        lineHeight: "40px",
                        maxWidth: "850px",
                        margin: "2rem auto",
                    }}
                >
                    👉 This isn't another generic "how to hire a developer" blog.
                </p>
                <p
                    style={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "22px",
                        lineHeight: "36px",
                        maxWidth: "850px",
                        margin: "0 auto",
                    }}
                >
                    👉 It's written by Plural Code's senior architects who've audited 100+ projects — the same checklist we use to evaluate real agencies for our clients.
                </p>
            </div>

            <div className="text-center mt-5">
                <button onClick={openModal} className="LM_link_btn_white w-auto">
                    Get Your Free E-Book
                </button>
            </div>
        </div>
    </section>
);

const BonusSection = ({ openModal }) => (
    <section
        className="pt-5"
        style={{
            backgroundImage: `url(${bg2})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }}
    >
        <div className="container">
            <div className="text-center mb-5">
                <h2 className="tc-primary head-font mb-4">Bonus for You:</h2>
                <p className="fs-20 text-muted mb-4" style={{ maxWidth: "800px", margin: "0 auto 2rem" }}>
                    After reading, you’ll also get an invite to our Free Software Audit Review — where we’ll assess your current proposal or idea and show you exactly what to fix before you spend a rupee
                </p>
                <br />
                <button onClick={openModal} className="LM_link_btn">
                    Get Your Free E-Book
                </button>
            </div>
        </div>
    </section>
);

const TrustSection = () => (
    <section className="py-5" style={{ backgroundColor: "#f8fafc" }}>
        <div className="container">
            <div className="row text-center justify-content-center">
                <div className="col-lg-10">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>

                                    ✅ Used by 200+ founders
                                </p>
                                <p className="text-muted mb-0">to pick reliable tech teams</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>

                                    🧠 Created by real architects
                                </p>
                                <p className="text-muted mb-0">not marketers</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="p-3">
                                <p className="fs-20" style={{ fontWeight: "600", color: "#0a2458", marginBottom: "2rem" }}>

                                    🔐 100% privacy
                                </p>
                                <p className="text-muted mb-0">guaranteed</p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </section>
);

const TestimonialSection = () => (
    <section
        className="py-75"
        style={{ backgroundColor: "#fff" }}
    >
        <div className="container">
            <div
                className="row justify-content-start"
                style={{ paddingTop: "50px", paddingLeft: "30px" }}
            >
                <div className="col-lg-10">
                    <div className="text-start mb-5">
                        <h3 className="tc-primary head-font mb-4" style={{
                            fontWeight: "bold",
                            fontStyle: "italic",

                        }}>
                            "This guide saved me from a ₹5L mistake!"
                        </h3>
                        <p
                            className="fs-20 mb-4"
                            style={{ maxWidth: "1045px", margin: "0 auto 2rem" }}
                        >
                            — Saurabh Jain, Founder, FinTech Startup


                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const FinalCtaSection = ({ openModal }) => (
    <section
        className="py-5"
        style={{
            backgroundImage: `linear-gradient(to right, rgba(11, 29, 81, 0.7), rgba(11, 29, 81, 0.7)), url(${blueBack})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
        }}
    >
        <div className="container">
            <div className="text-center text-white">
                <h2 className="tc-primary head-font mb-4 text-center text-white">
                    Take the First Step Toward a Safer Project
                </h2>
                <p className="fs-20 mb-4 text-center text-white" style={{ maxWidth: "700px", margin: "0 auto 2rem" }}>
                    Fill out the quick form below and get your free copy instantly. No spam. No sales calls. Just pure clarity.
                </p>

                <div className="text-center">
                    <button onClick={openModal} className="LM_link_btn_white">
                        Get Your Free E-Book
                    </button>
                </div>
            </div>
        </div>
    </section>
);

const LeadCaptureModal = ({
    isOpen,
    closeModal,
    formData,
    handleInputChange,
    showError,
    isSubmitting,
    submitLeadForm,
    offeringType,
    downloadLink,
    handleManualDownload,
    isFormSubmitted,
    nicheTypes = [],
}) => (
    <Modal
        open={isOpen}
        onClose={closeModal}
        aria-labelledby="lead-capture-modal"
        aria-describedby="lead-capture-form"
    >
        <Box sx={modalStyle}>
            <div className="text-center mb-4">
                <h4 className="tc-primary head-font">
                    Get Your Free The Smart Founder’s Guide to Choosing the Right Software Partner E-Book
                </h4>
                <p className="text-muted">
                    Enter your details below and we'll send the guide directly to your inbox.
                </p>
            </div>



            {(!isFormSubmitted || (offeringType === "E-Book")) && (
                <>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={showError && !formData.name.trim()}
                            helperText={
                                showError && !formData.name.trim() ? "Name is required" : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 100 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={showError && !formData.email.trim()}
                            helperText={
                                showError && !formData.email.trim()
                                    ? "Valid email is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 150 }}
                        />
                    </div>
                    <div className="mb-4">
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            type="text"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={showError && !formData.phone.trim()}
                            helperText={
                                showError && !formData.phone.trim()
                                    ? "Valid phone is required"
                                    : ""
                            }
                            margin="dense"
                            sx={{ "& input": { background: "#f8fafc" } }}
                            inputProps={{ maxLength: 10 }}
                        />
                    </div>

                    <div className="mb-4">
                        <Select
                            fullWidth
                            name="nicheType"
                            value={formData.nicheType}
                            onChange={handleInputChange}
                            displayEmpty
                            error={showError && !formData.nicheType}
                            sx={{
                                "& .MuiSelect-select": {
                                    background: "#e0e5ef",
                                    padding: "12px 14px",
                                },
                            }}
                        >
                            <MenuItem value="" disabled>
                                Industry Type
                            </MenuItem>
                            {nicheTypes?.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>

                        {showError && !formData.nicheType && (
                            <p
                                style={{
                                    color: "#d32f2f",
                                    fontSize: "0.75rem",
                                    margin: "3px 14px 0",
                                }}
                            >
                                Industry type is required
                            </p>
                        )}
                    </div>

                    {formData.nicheType === "Other" && (
                        <div className="mb-4">
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Specify Your Industry"
                                name="industry_other"
                                value={formData.industry_other}
                                onChange={handleInputChange}
                                error={
                                    showError &&
                                    formData.nicheType === "Other" &&
                                    !formData.industry_other.trim()
                                }
                                helperText={
                                    showError &&
                                        formData.nicheType === "Other" &&
                                        !formData.industry_other.trim()
                                        ? "Please specify your industry"
                                        : ""
                                }
                                required={formData.nicheType === "Other"}
                                margin="normal"
                                inputProps={{ maxLength: 100 }}
                            />
                        </div>
                    )}
                    <div className="d-flex gap-2">
                        <button
                            onClick={closeModal}
                            className="btn btn-outline-secondary flex-fill"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={submitLeadForm}
                            className="LM_link_btn1"
                            disabled={isSubmitting}
                            // style={{
                            //     background: "#1e3a8a",
                            //     border: "none",
                            // }}
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                                    Sending...
                                </>
                            ) : (
                                " Get Your Free E-Book"
                            )}
                        </button>
                    </div>
                    <p className="text-center text-muted small mt-3 mb-0">
                        100% privacy guaranteed. No spam ever.
                    </p>
                </>
            )}
        </Box>
    </Modal>
);



const ChoosingPartnerGuideLeadMagnet = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        nicheType: "",
        industry_other: "",
    });
    const [showError, setShowError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloadLink, setDownloadLink] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const { slug: paramSlug } = useParams();
    const location = useLocation();

    const nicheTypes = [
        "Manufacturing & Industrial",
        "Technology & Software",
        "Professional Services",
        "Retail & E-commerce",
        "Construction & Real Estate",
        "Logistics & Supply Chain",
        "Energy & Utilities",
        "Healthcare & Life Sciences",
        "Education & Nonprofit",
        "Finance & Insurance",
        "Media, Entertainment & Hospitality",
        "Other",
    ];

    const fallbackSlugMap = {
        "/lm/the-smart-founders-guide-to-choosing-the-right-software-partner-avoid-costly-mistakes": "the-smart-founders-guide-to-choosing-the-right-software-partner-avoid-costly-mistakes",
    };

    const actualSlug = paramSlug || fallbackSlugMap[location.pathname] || "";

    const searchParams = new URLSearchParams(location.search);
    const utmParams = {
        utm_source: searchParams.get("utm_source") || "direct",
        utm_medium: searchParams.get("utm_medium") || "email",
        utm_campaign: searchParams.get("utm_campaign") || "default",
    };

    const {
        apiData: leadMagnet,
        isLoading: isLeadMagnetLoding,
        error: leadMagnetError,
    } = useFetch(() => getLeadMagnet(actualSlug, utmParams), actualSlug);

    useEffect(() => {
        if (leadMagnetError) {
            cogoToast.error("Failed to load content");
        }
        if (leadMagnet?.status === "error") {
            cogoToast.error("Failed to load content");
        }
    }, [leadMagnetError, leadMagnet]);

    useEffect(() => {
        document.title = "Choosing a Software Partner - Plural Code Technologies";
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const openModal = () => setIsModalOpen(true);

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            name: "",
            email: "",
            phone: "",
            nicheType: "",
            industry_other: "",
        });
        setShowError(false);
    };

    const submitLeadForm = async () => {
        const { name, email, phone, nicheType, industry_other } = formData;

        if (!name.trim() || !email.trim() || !phone.trim() || !nicheType) {
            setShowError(true);
            cogoToast.info("Please fill in all required fields");
            return;
        }

        if (nicheType === "Other" && !industry_other.trim()) {
            setShowError(true);
            cogoToast.info("Please specify your industry");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setShowError(true);
            cogoToast.error("Please enter a valid email address");
            return;
        }

        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(phone)) {
            setShowError(true);
            cogoToast.error("Phone number must be exactly 10 digits");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                name,
                email,
                phone,
                slug: leadMagnet?.slug,
                nicheType,
                industry_other: nicheType === "Other" ? industry_other : "",
                offering_type: leadMagnet?.offering_type,
            };

            const response = await postLeadMagnetResponseForm(payload);

            if (response.status === "ok") {
                cogoToast.success(
                    "Thank you! Your E-Book will be sent to your email shortly."
                );
                setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    nicheType: "",
                    industry_other: "",
                });
                setShowError(false);
                setIsFormSubmitted(true);
            } else {
                cogoToast.error(
                    response.message || "Something went wrong. Please try again."
                );
            }
        } catch (err) {
            console.error("Form submission error:", err);
            cogoToast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };



    const handleManualDownload = async () => {
        if (!downloadLink) {
            cogoToast.error("No download token available");
            return;
        }

        try {
            const downloadResult = await downloadLeadMagnet(actualSlug, downloadLink);

            if (downloadResult.success) {
                const link = document.createElement("a");
                link.href = downloadResult.fileUrl;
                link.download = downloadResult.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                cogoToast.success("Download started! Check your Downloads folder.");
                setDownloadLink("");

                setTimeout(() => {
                    window.URL.revokeObjectURL(downloadResult.fileUrl);
                }, 30000);
            } else {
                cogoToast.error(
                    downloadResult.error || "Download failed. Please contact our team."
                );
                setDownloadLink("");
            }
        } catch (error) {
            console.error("Download error:", error);
            cogoToast.error("Download failed. Please try again.");
        }
    };

    if (isLeadMagnetLoding) {
        return (
            <div
                className="container text-center"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </div>
        );
    }

    if (leadMagnetError) {
        return (
            <div className="container text-center py-5">
                <h3>Error loading the page</h3>
                <p>{leadMagnetError.message || "Unknown error occurred"}</p>
            </div>
        );
    }

    return (
        <div>
            <Helmet>
                <meta name="title" content={leadMagnet?.meta_title || "Choosing a Software Partner - Plural Code Technologies"} />
                <meta name="description" content={leadMagnet?.meta_description || "Learn how to choose the right software partner with our free founder's guide"} />
                <meta
                    property="og:description"
                    content={leadMagnet?.meta_description || "Learn how to choose the right software partner with our free founder's guide"}
                />
            </Helmet>
            <main className="wrapper">
                <HeroSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    nicheTypes={nicheTypes}
                    offeringType={leadMagnet?.offering_type}
                />

                <WhatYouWillLearn openModal={openModal} />

                <WhyItsDifferent openModal={openModal} />

                <WhoIsThisFor openModal={openModal} />

                <BonusSection openModal={openModal} />

                <TrustSection />

                <FinalCtaSection openModal={openModal} />

                <TestimonialSection />

                <LeadCaptureModal
                    isOpen={isModalOpen}
                    closeModal={closeModal}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    showError={showError}
                    isSubmitting={isSubmitting}
                    submitLeadForm={submitLeadForm}
                    downloadLink={downloadLink}
                    handleManualDownload={handleManualDownload}
                    isFormSubmitted={isFormSubmitted}
                    offeringType={leadMagnet?.offering_type}
                    nicheTypes={nicheTypes}
                />
            </main>
        </div>
    );
};

export default ChoosingPartnerGuideLeadMagnet;